import { useEffect, useMemo, useState } from 'react';
import { Eye, PenLine, Search } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle } from '@/components/ui/view-mode-toggle';
import { PermissionDenied } from '@/components/ui/permission-denied';
import type { ApiError } from '@/core/api';
import { createCabinetViewStore, viewModes } from '@/features/cabinet/store/cabinetView';
import {
  DOCUMENT_STATUSES,
  DOCUMENT_STATUS_LABEL,
  NATIVE_SIGNABLE,
  TEMPLATE_TYPE_LABEL,
  type DocumentStatus,
} from '../constants';
import { fetchDocumentFileBlob } from '../api';
import { DocumentVersionsPanel } from '../components/DocumentVersionsPanel';
import { useDocuments, useSignDocument } from '../queries';
import type { Document } from '../types';

const ALL = 'all';
const MODES = viewModes(['table', 'list', 'cards', 'grouped']);
const useFilesUi = createCabinetViewStore('documents.files:ui', 'table');

const STATUS_VARIANT: Record<DocumentStatus, 'secondary' | 'outline' | 'destructive'> = {
  draft: 'outline',
  rendered: 'outline',
  pending_signature: 'secondary',
  signed: 'secondary',
  void: 'destructive',
};

const fmtDate = (d: string) => new Date(d).toLocaleDateString();

export function DocumentsListPage() {
  const view = useFilesUi((s) => s.view);
  const setView = useFilesUi((s) => s.setView);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | typeof ALL>(ALL);
  const [viewing, setViewing] = useState<Document | null>(null);
  // Auth-fetch the file as a blob (raw media `src` 401s / fails CORS cross-origin).
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const viewingId = viewing?.id ?? null;
  const viewingHasFile = Boolean(viewing?.file);
  useEffect(() => {
    if (!viewingId || !viewingHasFile) return;
    let cancelled = false;
    let url: string | null = null;
    fetchDocumentFileBlob(viewingId)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setFileUrl(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
      setFileUrl(null);
    };
  }, [viewingId, viewingHasFile]);

  const { data, isPending, isError, error, isFetching } = useDocuments({
    search: search || undefined,
    status: statusFilter === ALL ? undefined : statusFilter,
  });
  const sign = useSignDocument();
  const documents = data?.results ?? [];

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput.trim());
  }

  const canSign = (doc: Document) =>
    NATIVE_SIGNABLE.includes(doc.status) && doc.signing_method === 'native';

  const actions = (doc: Document) => (
    <>
      <Button size="icon" variant="ghost" aria-label="View document" onClick={() => setViewing(doc)}>
        <Eye className="size-4" />
      </Button>
      {canSign(doc) ? (
        <Button size="sm" variant="ghost" onClick={() => sign.mutate(doc.id)} disabled={sign.isPending}>
          <PenLine className="size-4" /> Sign
        </Button>
      ) : null}
    </>
  );

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Documents</h1>

      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={applySearch} className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute top-2.5 left-2.5 size-4" />
          <Input
            className="pl-8"
            placeholder="Search documents"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </form>
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as DocumentStatus | typeof ALL)}
        >
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {DOCUMENT_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ViewModeToggle value={view} onChange={setView} modes={MODES} className="ml-auto" />
      </div>

      {isError ? (
        (error as unknown as ApiError)?.status === 403 ? (
          <PermissionDenied message={(error as unknown as ApiError)?.message} />
        ) : (
          <p className="text-destructive text-sm">
            {(error as unknown as ApiError)?.message ?? 'Failed to load documents.'}
          </p>
        )
      ) : isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : view === 'table' ? (
        <FilesTable rows={documents} isFetching={isFetching} renderActions={actions} />
      ) : view === 'list' ? (
        <FilesList rows={documents} renderActions={actions} />
      ) : view === 'cards' ? (
        <FilesCards rows={documents} renderActions={actions} />
      ) : (
        <FilesGroupedByStatus rows={documents} renderActions={actions} />
      )}

      <Dialog open={Boolean(viewing)} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{viewing?.name}</DialogTitle>
            <DialogDescription>
              {viewing ? DOCUMENT_STATUS_LABEL[viewing.status] : ''}
              {viewing?.template_version ? ` · template v${viewing.template_version}` : ''}
            </DialogDescription>
          </DialogHeader>
          {viewing?.file ? (
            fileUrl ? (
              <iframe
                src={fileUrl}
                title={viewing.name}
                className="h-[70vh] w-full rounded-md border"
              />
            ) : (
              <Skeleton className="h-[70vh] w-full" />
            )
          ) : (
            <pre className="bg-muted max-h-[60vh] overflow-auto rounded-md p-4 text-sm whitespace-pre-wrap">
              {viewing?.rendered_content || 'No rendered content.'}
            </pre>
          )}
          {viewing ? <DocumentVersionsPanel documentId={viewing.id} enabled={Boolean(viewing)} /> : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatusBadge = ({ status }: { status: DocumentStatus }) => (
  <Badge variant={STATUS_VARIANT[status]}>{DOCUMENT_STATUS_LABEL[status]}</Badge>
);

interface ViewProps {
  rows: Document[];
  renderActions: (doc: Document) => React.ReactNode;
}

const EmptyText = () => (
  <p className="text-muted-foreground text-sm">No documents yet. Render one from the Template Library.</p>
);

function FilesTable({ rows, isFetching, renderActions }: ViewProps & { isFetching: boolean }) {
  return (
    <div className="rounded-md border" aria-busy={isFetching}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="w-28 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                No documents yet. Render one from the Template Library.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell className="font-medium">{doc.name}</TableCell>
                <TableCell>{TEMPLATE_TYPE_LABEL[doc.type]}</TableCell>
                <TableCell>
                  <StatusBadge status={doc.status} />
                </TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(doc.created)}</TableCell>
                <TableCell className="text-right">{renderActions(doc)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function FilesList({ rows, renderActions }: ViewProps) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <ul className="divide-y rounded-md border">
      {rows.map((doc) => (
        <li key={doc.id} className="flex items-center gap-3 px-3 py-2 text-sm">
          <span className="min-w-0 flex-1 truncate font-medium">{doc.name}</span>
          <span className="text-muted-foreground hidden sm:inline">{TEMPLATE_TYPE_LABEL[doc.type]}</span>
          <StatusBadge status={doc.status} />
          <span className="flex items-center">{renderActions(doc)}</span>
        </li>
      ))}
    </ul>
  );
}

function FilesCards({ rows, renderActions }: ViewProps) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((doc) => (
        <Card key={doc.id} className="min-w-0">
          <CardHeader>
            <CardTitle className="flex min-w-0 items-center justify-between gap-2 text-base">
              <span className="min-w-0 flex-1 truncate">{doc.name}</span>
              <span className="shrink-0">
                <StatusBadge status={doc.status} />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
            <span>
              {TEMPLATE_TYPE_LABEL[doc.type]} · {fmtDate(doc.created)}
            </span>
            <span className="flex items-center">{renderActions(doc)}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function FilesGroupedByStatus({ rows, renderActions }: ViewProps) {
  const groups = useMemo(
    () =>
      DOCUMENT_STATUSES.map((s) => ({
        key: s.value,
        label: s.label,
        bucket: rows.filter((r) => r.status === s.value),
      })).filter((g) => g.bucket.length > 0),
    [rows],
  );
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => (
        <section key={key}>
          <h2 className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase">
            {label}
            <Badge variant="outline" className="text-[10px]">
              {bucket.length}
            </Badge>
          </h2>
          <FilesCards rows={bucket} renderActions={renderActions} />
        </section>
      ))}
    </div>
  );
}
